import { Button, Checkbox, Modal, message, Tooltip } from 'antd';
import type { FC } from 'react';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import TableView from '@/components/folders/TableView';
import { Documentcolumn } from '@/components/columns/DocumentsColumn';
import { DocumentView } from '@/features/documents/Sidebar/DocumentView';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { AppConstants } from '@/utils/constants';
import { useAppContext } from '@/context';
import { useTranslation } from 'react-i18next';
import {
  FolderSelectionState,
  isFileRecord,
} from '../selection/selectionModel';
import { useInquirySelection } from '../selection/useInquirySelection';
import useMomentWithTimezone from '@/hooks/timezone';

interface ModalAddFoldersProps {
  inquiryId: string;
  inquiry: any;
  setReloadInquiry: any;
  open: boolean;
  onCancel: () => void;
  reloadData: boolean;
}

const isProcessingFileRecord = (record: any): boolean => {
  if (!isFileRecord(record)) {
    return false;
  }

  const status = record?.status || record?.extras?.status || '';

  return (
    status === 'pending' ||
    status === 'queued' ||
    status === 'processing' ||
    record.processed === false ||
    record.processed === undefined
  );
};

const ModalAddFolders: FC<ModalAddFoldersProps> = ({
  inquiryId,
  inquiry,
  setReloadInquiry,
  open,
  onCancel,
  reloadData,
}) => {
  const { t } = useTranslation();
  const { user } = useAppContext();
  const momentWithTimezone = useMomentWithTimezone();
  const [viewRevision, setViewRevision] = useState<number>(0);
  const [documentView] = useState(
    () => new DocumentView(user || {}, () => setViewRevision(prev => prev + 1)),
  );
  const [currentNode, setCurrentNode] = useState<any>(null);
  const [usersInDocuments, setUsersInDocuments] = useState<any>({});
  const [messageApi, contextHolder] = message.useMessage();

  const initializedRef = useRef<boolean>(false);
  const lastReloadDataRef = useRef<boolean>(reloadData);
  const currentNodeRef = useRef<any>(null);
  currentNodeRef.current = currentNode;

  const handlePersistError = useCallback(
    (error?: string) => {
      messageApi.error(error || t('home.chat.attachments.folders.saveError'));
    },
    [messageApi, t],
  );

  const handleExternalChange = useCallback(() => {
    messageApi.info(t('home.chat.attachments.folders.externalChange'));
  }, [messageApi, t]);

  const selection = useInquirySelection({
    inquiryId,
    inquiry,
    onPersistError: handlePersistError,
    onExternalChange: handleExternalChange,
  });

  /**
   * The folder tree is fetched once and reused across openings, so reopening
   * the selector does not discard the loaded tree or reset the current folder.
   * A change to `reloadData` refreshes the folder currently in view.
   */
  useEffect(() => {
    if (!open) {
      return;
    }

    const reloadDataChanged = lastReloadDataRef.current !== reloadData;
    lastReloadDataRef.current = reloadData;

    if (!initializedRef.current) {
      initializedRef.current = true;
      void documentView.init();
      return;
    }

    if (reloadDataChanged) {
      const node = currentNodeRef.current;
      void documentView.refreshData(node?.id ? { folder_id: node.id } : {});
    }
  }, [open, reloadData, documentView]);

  /**
   * Resolve the descendants of every folder in view in a single request so
   * their selection state can be derived without a request per folder.
   */
  const { ensureFolderStates } = selection;

  useEffect(() => {
    if (!open) {
      return;
    }

    const folderIds = (documentView.tableData || [])
      .filter((item: any) => item && !isFileRecord(item))
      .map((item: any) => String(item.id));

    if (folderIds.length > 0) {
      void ensureFolderStates(folderIds);
    }
  }, [open, viewRevision, documentView, ensureFolderStates]);

  const buildParentChain = (targetNode: any, treeData: any[]): any => {
    const findNodeWithPath = (
      nodeId: string,
      nodes: any[],
      path: any[] = [],
    ): { node: any; path: any[] } | null => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          return { node, path };
        }
        if (node.children) {
          const result = findNodeWithPath(nodeId, node.children, [
            ...path,
            node,
          ]);
          if (result) return result;
        }
      }
      return null;
    };

    const result = findNodeWithPath(targetNode.id, treeData);
    if (!result) return null;

    const { node, path } = result;

    if (path.length === 0) {
      return { ...node, parent: null };
    }

    const buildChain = (index: number): any => {
      if (index < 0) {
        return { ...node, parent: null };
      }
      const parentNode = { ...path[index] };
      parentNode.parent = index === 0 ? null : buildChain(index - 1);
      return parentNode;
    };

    const targetNodeWithParent = { ...node };
    targetNodeWithParent.parent = buildChain(path.length - 1);

    return targetNodeWithParent;
  };

  const onLoadData = async (node: any, isBack: boolean = false) => {
    if (isBack && node === null) {
      documentView.backToNode(null);
      documentView.tableData = documentView.treeData.filter(
        (item: any) => !item.parent,
      );
      setCurrentNode(null);
      setViewRevision(prev => prev + 1);
      return;
    }

    if (!node || isFileRecord(node)) {
      return;
    }

    if (isBack) {
      documentView.backToNode(node);
      const nodeWithParent = buildParentChain(node, documentView.treeData);
      setCurrentNode(nodeWithParent || node);
      return;
    }

    if (node.children) {
      documentView.tableData = node.children;
      const nodeWithParent = buildParentChain(node, documentView.treeData);
      setCurrentNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setViewRevision(prev => prev + 1);
      return;
    }

    if (node.id !== currentNode?.id) {
      await documentView.onLoadData(node);
      const updatedNode = documentView.findNodeInTree(
        node.id,
        documentView.treeData,
      );
      const nodeWithParent = updatedNode
        ? buildParentChain(updatedNode, documentView.treeData)
        : buildParentChain(node, documentView.treeData);
      setCurrentNode(
        nodeWithParent || (node.isRoot ? { ...node, parent: null } : node),
      );
      setViewRevision(prev => prev + 1);
    }
  };

  /**
   * Closing persists anything still pending, then lets the chat screen pick up
   * the committed selection.
   */
  const handleClose = async () => {
    onCancel();
    await selection.flush();
    setReloadInquiry((prev: boolean) => !prev);
  };

  const folderStateLabel = (state: FolderSelectionState): string => {
    if (state === 'full') {
      return t('home.chat.attachments.folders.stateFull');
    }
    if (state === 'partial') {
      return t('home.chat.attachments.folders.statePartial');
    }
    return t('home.chat.attachments.folders.stateNone');
  };

  const renderSaveStatus = () => {
    if (selection.status === 'saving') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <LoadingOutlined />
          {t('home.chat.attachments.folders.saving')}
        </span>
      );
    }

    if (selection.status === 'error') {
      return (
        <span
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#cf1322',
          }}
        >
          <ExclamationCircleOutlined />
          {t('home.chat.attachments.folders.saveError')}
          <Button size="small" onClick={() => void selection.retry()}>
            {t('home.chat.attachments.folders.retry')}
          </Button>
        </span>
      );
    }

    if (selection.status === 'saved') {
      return (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#389e0d',
          }}
        >
          <CheckCircleOutlined />
          {t('home.chat.attachments.folders.saved')}
        </span>
      );
    }

    return (
      <span>
        {t('home.chat.attachments.folders.selectedCount', {
          count: selection.selectedFileCount,
        })}
      </span>
    );
  };

  return (
    <div>
      <Modal
        title={t('home.chat.attachments.folders.title')}
        width={'80%'}
        open={open}
        onCancel={() => void handleClose()}
        footer={null}
        style={{ overflow: 'auto' }}
      >
        <TableView
          tableData={documentView.tableData}
          currentNode={currentNode}
          setCurrentNode={setCurrentNode}
          usersInDocuments={usersInDocuments}
          setUsersInDocuments={setUsersInDocuments}
          onExpand={onLoadData}
          columns={[
            ...(
              Documentcolumn(
                t,
                onLoadData,
                user?.scopes?.includes(AppConstants.adminRole),
                momentWithTimezone,
                setCurrentNode,
              ) as any
            ).map((col: any) => {
              if (col.key === 'name') {
                return { ...col, width: '40%' };
              }
              return col;
            }),
            {
              title: () => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <Button
                    size="small"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      void selection.setRecordsSelected(
                        (documentView.tableData || []).filter(
                          record => !isProcessingFileRecord(record),
                        ),
                        true,
                      );
                    }}
                  >
                    {t('home.chat.attachments.folders.selectAll')}
                  </Button>
                  <Button
                    size="small"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      void selection.setRecordsSelected(
                        documentView.tableData || [],
                        false,
                      );
                    }}
                  >
                    {t('home.chat.attachments.folders.removeAll')}
                  </Button>
                </div>
              ),
              key: 'action',
              width: '20%',
              align: 'center',
              render: (_: any, record: any) => {
                const recordId = String(record.id);
                const isFile = isFileRecord(record);
                const folderState = isFile
                  ? null
                  : selection.getFolderState(recordId);

                const isChecked = isFile
                  ? selection.isFileSelected(recordId)
                  : folderState === 'full';
                const isIndeterminate = folderState === 'partial';

                const isProcessing = isProcessingFileRecord(record);

                // A folder whose contents are unknown must not be presented as
                // a confirmed "not selected" state.
                const isUnresolved =
                  !isFile && !selection.isFolderResolved(recordId);

                const stateLabel = isFile
                  ? isChecked
                    ? t('home.chat.attachments.folders.deselectFile')
                    : t('home.chat.attachments.folders.selectFile')
                  : folderStateLabel(folderState as FolderSelectionState);

                return (
                  <div
                    onClick={(e: any) => e.stopPropagation()}
                    onClickCapture={(e: any) => {
                      if (isProcessing) {
                        e.stopPropagation();
                        e.preventDefault();
                        messageApi.warning(
                          t(
                            'home.chat.attachments.files.uploadInProgressWarning',
                          ),
                        );
                      }
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip
                      title={
                        isProcessing
                          ? t('home.documents.table.status.processing')
                          : stateLabel
                      }
                    >
                      <Checkbox
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        disabled={isProcessing || isUnresolved}
                        aria-label={stateLabel}
                        aria-busy={isUnresolved}
                        onChange={(e: CheckboxChangeEvent) => {
                          e.stopPropagation();
                          const shouldSelect = e.target.checked;

                          if (isFile) {
                            selection.setFileSelected(recordId, shouldSelect);
                            return;
                          }

                          void selection.setFolderSelected(
                            recordId,
                            shouldSelect,
                          );
                        }}
                      />
                    </Tooltip>
                  </div>
                );
              },
            },
          ]}
          button={renderSaveStatus()}
        />
      </Modal>
      {contextHolder}
    </div>
  );
};

export default ModalAddFolders;
